// src/pages/pipedrive/Deals.js
import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiArchive,
  FiArrowUp,
  FiCheck,
  FiChevronDown,
  FiChevronUp,
  FiDownload,
  FiFilter,
  FiInfo,
  FiList,
  FiLoader,
  FiMoreHorizontal,
  FiPlus,
  FiRefreshCw,
  FiSearch,
  FiSettings,
  FiAlertTriangle,
  FiColumns,
  FiUser,
} from "react-icons/fi";
import { apiGet, apiPatch, apiDelete } from "../../utils/apiFetch";
import apiPath from "../../utils/pathObj";
import AddDeal from "./AddDeal";
import { daysSince, getOpenedLeadIds, isLeadOpened, markLeadOpened } from "./openedLeads";
import { resolveOrgName, resolveOwnerName, resolvePersonName } from "./pipedriveHelpers";

// ═══════════════════════════════════════════════════════════════
// MAPPERS
// ═══════════════════════════════════════════════════════════════
const mapDealFromApi = (d) => ({
  id: String(d.id),
  title: d.title || "(Untitled deal)",
  value: d.value ?? 0,
  currency: d.currency || "USD",
  stageId: d.stage_id != null ? String(d.stage_id) : "",
  pipelineId: d.pipeline_id,
  owner: d.owner_id,
  ownerName: resolveOwnerName(d),
  status: d.status || "open",
  orgName: resolveOrgName(d),
  personName: resolvePersonName(d),
  expectedCloseDate: d.expected_close_date || "",
  nextActivityDate: d.next_activity_date || "",
  addTime: d.add_time || d.created_at || "",
  updateTime: d.update_time || d.updated_at || "",
  stageChangeTime: d.stage_change_time || d.update_time || d.add_time || "",
  hasNextActivity: Boolean(d.next_activity_id || d.next_activity_date),
  isArchived: d.is_archived === true || d.is_archived === 1,
  doneActivities: Number(d.done_activities_count ?? d.activities_done_count ?? 0) || 0,
  activitiesToDo: Number(d.undone_activities_count ?? d.activities_to_do ?? 0) || 0,
  productsCount: Number(d.products_count ?? d.product_count ?? 0) || 0,
  lastEmailSent: d.last_outgoing_mail_time || d.last_email_sent || "",
  lastEmailReceived: d.last_incoming_mail_time || d.last_email_received || "",
  raw: d,
});

const mapStageFromApi = (s) => ({
  id: String(s.id),
  title: s.name,
  pipelineId: s.pipeline_id,
  order: s.order_nr,
  daysToRotten: s.days_to_rotten > 0 ? Number(s.days_to_rotten) : 7,
  rottenFlag: s.rotten_flag !== false,
});

const FALLBACK_COLUMNS = [
  { id: "qualified", title: "Qualified" },
  { id: "contact_made", title: "Contact Made" },
  { id: "prospect_qualified", title: "Prospect Qualified" },
  { id: "needs_defined", title: "Needs Defined" },
];

/** Pipedrive-like fixed column width so every deal card is the same size */
const KANBAN_COLUMN_WIDTH = "w-[240px]";

/**
 * Pipedrive-style fonts (active).
 * OLD classes kept in comments — swap back if the new look is wrong.
 */
const F = {
  // OLD page: "font-sans text-gray-800"
  page: "font-sans text-[13px] text-[#292d38]",
  // OLD: "text-sm font-semibold text-gray-800"
  colTitle: "text-[14px] font-semibold text-[#222222]",
  // OLD: "text-xs text-gray-500"
  colMeta: "text-[12px] text-[#666666]",
  // OLD: "text-[13px] leading-snug font-semibold ... text-[#21232c]"
  cardTitle: "text-[14px] leading-snug font-semibold text-[#21232c]",
  cardTitleRotten: "text-[14px] leading-snug font-semibold text-[#8b3a3a]",
  // OLD: "text-xs ... text-gray-500"
  cardSub: "text-[12px] text-[#717171]",
  cardSubRotten: "text-[12px] text-[#a85a5a]",
  // OLD: "text-xs text-gray-500"
  cardMeta: "text-[12px] text-[#888888]",
  // OLD: "text-sm" / "text-sm font-medium text-gray-700"
  toolbar: "text-[13px]",
  toolbarMuted: "text-[13px] font-medium text-gray-700",
  // OLD: "text-[13px] font-semibold text-[#292d38]"
  tableHeader: "text-[13px] font-semibold text-[#292d38]",
  // OLD: "text-[#292d38]" / hot "text-[#c94a5a] font-semibold"
  tableCell: "text-[13px] text-[#292d38]",
  tableTitle: "text-[13px] font-normal text-[#292d38]",
  tableTitleHot: "text-[13px] font-semibold text-[#c94a5a]",
  badge: "text-[10px] font-bold text-white",
};

/** Filter menu presets (matches Pipedrive Filter screenshot) */
const FILTER_PRESETS = [
  { key: "lost", label: "All lost deals", params: { preset: "lost" } },
  { key: "open", label: "All open deals", params: { preset: "open" } },
  { key: "won", label: "All won deals", params: { preset: "won" } },
  { key: "older_3_months", label: "More than 3 months old deals", params: { preset: "older_3_months" } },
  { key: "rotten", label: "Rotten deals", params: { preset: "rotten" } },
];

/** Add condition → Suggested */
const FILTER_CONDITIONS = [
  { key: "value_gte", label: "Value >= 2", params: { condition: "value_gte", value: 2 } },
  { key: "expected_close_this_month", label: "Expected close date is this month", params: { condition: "expected_close_this_month" } },
  { key: "created_this_week", label: "Deal created is this week", params: { condition: "created_this_week" } },
  { key: "next_activity_before_today", label: "Next activity date is before today", params: { condition: "next_activity_before_today" } },
  { key: "last_activity_before_1_week", label: "Last activity date is before 1 week ago", params: { condition: "last_activity_before_1_week" } },
];

/** Sort by options (Pipedrive screenshot) */
const SORT_OPTIONS = [
  { key: "next_activity", label: "Next activity (default)" },
  { key: "title", label: "Deal title" },
  { key: "value", label: "Deal value" },
  { key: "person", label: "Linked person" },
  { key: "org", label: "Linked organization" },
  { key: "expected_close", label: "Expected close date" },
  { key: "created", label: "Deal created" },
  { key: "updated", label: "Deal update time" },
  { key: "done_activities", label: "Done activities" },
  { key: "activities_todo", label: "Activities to do" },
  { key: "products", label: "Number of products" },
  { key: "owner", label: "Owner name" },
  { key: "last_email_sent", label: "Last email sent" },
  { key: "last_email_received", label: "Last email received" },
];

const sortLabel = (key) => SORT_OPTIONS.find((o) => o.key === key)?.label?.replace(" (default)", "") || "Next activity";

const toTime = (v) => {
  if (!v) return 0;
  const t = new Date(v).getTime();
  return Number.isNaN(t) ? 0 : t;
};

const cmpStr = (a, b) => String(a || "").localeCompare(String(b || ""), undefined, { sensitivity: "base" });

// ═══════════════════════════════════════════════════════════════
// DROPDOWN
// ═══════════════════════════════════════════════════════════════
const Dropdown = ({ trigger, children, align = "left", width = "w-48" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) setIsOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  const close = () => setIsOpen(false);
  return (
    <div className="relative inline-block text-left" ref={dropdownRef}>
      <div onClick={() => setIsOpen(!isOpen)} className="cursor-pointer h-full">{trigger}</div>
      {isOpen && (
        <div
          className={`absolute mt-1 ${width} rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50 ${
            align === "right" ? "right-0" : "left-0"
          }`}
        >
          <div className="py-1" role="menu" onClick={close}>
            {typeof children === "function" ? children({ close }) : children}
          </div>
        </div>
      )}
    </div>
  );
};

const DropdownItem = ({ children, onClick, active = false }) => (
  <button
    type="button"
    onClick={onClick}
    className={`w-full text-left flex items-center justify-between gap-2 px-4 py-2 text-sm hover:bg-gray-100 ${
      active ? "text-gray-900 font-semibold" : "text-gray-700"
    }`}
  >
    <span>{children}</span>
    {active ? <FiCheck className="text-blue-600 shrink-0" size={16} /> : null}
  </button>
);

// ═══════════════════════════════════════════════════════════════
// ROW ACTIONS
// ═══════════════════════════════════════════════════════════════
const RowActions = ({ onDelete, onArchive, onUnarchive, mode = "list" }) => {
  const [open, setOpen] = useState(false);
  const btnRef = useRef(null);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const isArchive = mode === "archive";

  const toggle = (e) => {
    e.stopPropagation();
    if (!open && btnRef.current) {
      const r = btnRef.current.getBoundingClientRect();
      setPos({ top: r.bottom + 4, left: Math.max(8, r.right - 144) });
    }
    setOpen((v) => !v);
  };

  return (
    <div className="relative inline-flex" onClick={(e) => e.stopPropagation()}>
      <button
        ref={btnRef}
        type="button"
        onClick={toggle}
        className="rounded p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600"
      >
        <FiMoreHorizontal size={18} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-[60]" onClick={(e) => { e.stopPropagation(); setOpen(false); }} />
          <div
            className="fixed z-[70] w-36 rounded-md border border-gray-100 bg-white py-1 text-left shadow-lg"
            style={{ top: pos.top, left: pos.left }}
          >
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setOpen(false); onDelete?.(); }}
              className="w-full px-3 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-100"
            >
              Delete
            </button>
            {isArchive ? (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setOpen(false); onUnarchive?.(); }}
                className="w-full px-3 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-100"
              >
                Unarchive
              </button>
            ) : (
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setOpen(false); onArchive?.(); }}
                className="w-full px-3 py-1.5 text-left text-sm text-gray-700 hover:bg-gray-100"
              >
                Archive
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// HELPERS
// ═══════════════════════════════════════════════════════════════
const formatMoney = (value, currency = "USD") => {
  const num = Number(value) || 0;
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency, maximumFractionDigits: 0 }).format(num);
  } catch { return `$${num.toLocaleString()}`; }
};
const formatDate = (d) => {
  if (!d) return "";
  try {
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return d;
    return dt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  } catch { return d; }
};

// ═══════════════════════════════════════════════════════════════
// LIST VIEW
// ═══════════════════════════════════════════════════════════════
const ListView = ({
  deals,
  onAddDeal,
  onImportDeals,
  onDeleteDeal,
  onArchiveDeal,
  onUnarchiveDeal,
  onOpenDeal,
  openedIds,
  mode = "list",
}) => {
  if (!deals.length) {
    if (mode === "archive") {
      return (
        <div className="flex-1 flex flex-col items-center justify-center h-full bg-white">
          <FiArchive size={28} className="text-gray-400" />
          <h1 className="text-xl font-semibold text-gray-800 mt-3">No archived deals</h1>
          <p className="text-gray-500 mt-2 text-sm">Deleted / archived deals will appear here.</p>
        </div>
      );
    }
    return (
      <div className="flex-1 flex flex-col items-center justify-center h-full bg-white">
        <h1 className="text-2xl font-semibold text-gray-800">Add your first deal</h1>
        <p className="text-gray-500 mt-2 mb-8">Track your deals and manage your pipeline to close more business.</p>
        <div className="flex space-x-3 mb-16">
          <button onClick={onAddDeal} className="px-4 py-2 text-sm font-medium border border-gray-300 rounded hover:bg-gray-50 flex items-center shadow-sm text-gray-700">
            <FiPlus className="mr-1.5" size={16} /> Deal
          </button>
          <button onClick={onImportDeals} className="px-4 py-2 text-sm font-medium border border-gray-300 rounded hover:bg-gray-50 flex items-center shadow-sm text-gray-700">
            <FiDownload className="mr-1.5" size={16} /> Import deals
          </button>
        </div>
      </div>
    );
  }

  const isArchive = mode === "archive";
  // Archive sized like Leads table; List keeps wider Pipedrive columns
  const colW = isArchive
    ? ["40px", "180px", "90px", "140px", "140px", "130px", "130px", "120px", "44px"]
    : ["36px", "312px", "120px", "220px", "200px", "160px", "160px", "140px", "42px"];
  const cellAlign = isArchive ? "text-center" : "text-left";
  const valueAlign = isArchive ? "text-center" : "text-right";

  return (
    <div className="h-full overflow-x-auto overflow-y-auto no-scrollbar bg-white">
      <div className="overflow-x-auto no-scrollbar rounded-sm border border-gray-200 bg-white w-full">
        <table
          className={`w-full table-fixed border-collapse text-sm ${
           isArchive ? "text-center" : "min-w-[1200px]"
          }`}
        >
          <colgroup>
            {colW.map((w, i) => (
              <col key={i} style={{ width: w }} />
            ))}
          </colgroup>

          <thead className="sticky top-0 bg-white z-10">
            {/* OLD: text-[13px] font-semibold text-[#292d38] / text-left text-[14px] */}
            <tr className={`${isArchive ? "text-center" : "text-left"} ${F.tableHeader}`}>
              <th className="border-b border-r border-gray-200 px-2 py-2.5">
                <input type="checkbox" className="cursor-pointer rounded border-gray-300 accent-blue-600" onClick={(e) => e.stopPropagation()} />
              </th>
              <th className="border-b border-r border-gray-200 px-2.5 py-2.5">Title</th>
              <th className="border-b border-r border-gray-200 px-2.5 py-2.5">Value</th>
              <th className="border-b border-r border-gray-200 px-2.5 py-2.5">Organization</th>
              <th className="border-b border-r border-gray-200 px-2.5 py-2.5">Contact person</th>
              <th className="border-b border-r border-gray-200 px-2.5 py-2.5">Expected close date</th>
              <th className="border-b border-r border-gray-200 px-2.5 py-2.5">Next activity date</th>
              <th className="border-b border-r border-gray-200 px-2.5 py-2.5">Owner</th>
              <th className="border-b border-gray-200 px-2 py-2.5 text-center">
                <button type="button" title="Table settings" className="inline-flex items-center justify-center text-gray-500 hover:text-gray-800">
                  <FiSettings size={18} />
                </button>
              </th>
            </tr>
          </thead>

          <tbody>
            {deals.map((deal) => {
              const opened = openedIds?.has?.(deal.id) || isLeadOpened(deal.id);
              return (
                <tr key={deal.id} onClick={() => onOpenDeal?.(deal)} className="group h-[42px] cursor-pointer hover:bg-gray-50">
                  <td className="border-b border-r border-gray-200 px-2 py-1.5 text-center">
                    <input type="checkbox" className="cursor-pointer rounded border-gray-300 accent-blue-600" onClick={(e) => e.stopPropagation()} />
                  </td>
                  <td
                    /* OLD: opened ? "text-[#292d38] font-normal" : "text-[#c94a5a] font-semibold" */
                    className={`overflow-hidden text-ellipsis whitespace-nowrap border-b border-r border-gray-200 px-2.5 py-1.5 ${cellAlign} ${
                      opened ? F.tableTitle : F.tableTitleHot
                    }`}
                    title={deal.title}
                  >
                    {deal.title || "—"}
                  </td>
                  {/* OLD cells: text-[#292d38] */}
                  <td className={`whitespace-nowrap border-b border-r border-gray-200 px-2.5 py-1.5 ${F.tableCell} ${valueAlign}`}>
                    {formatMoney(deal.value, deal.currency)}
                  </td>
                  <td className={`overflow-hidden text-ellipsis whitespace-nowrap border-b border-r border-gray-200 px-2.5 py-1.5 ${F.tableCell} ${cellAlign}`} title={deal.orgName}>
                    {deal.orgName || ""}
                  </td>
                  <td className={`overflow-hidden text-ellipsis whitespace-nowrap border-b border-r border-gray-200 px-2.5 py-1.5 ${F.tableCell} ${cellAlign}`} title={deal.personName}>
                    {deal.personName || ""}
                  </td>
                  <td className={`whitespace-nowrap border-b border-r border-gray-200 px-2.5 py-1.5 ${F.tableCell} ${cellAlign}`}>
                    {deal.expectedCloseDate ? formatDate(deal.expectedCloseDate) : ""}
                  </td>
                  <td className={`whitespace-nowrap border-b border-r border-gray-200 px-2.5 py-1.5 ${F.tableCell} ${cellAlign}`}>
                    {deal.nextActivityDate ? formatDate(deal.nextActivityDate) : ""}
                  </td>
                  <td className={`overflow-hidden text-ellipsis whitespace-nowrap border-b border-r border-gray-200 px-2.5 py-1.5 ${F.tableCell} ${cellAlign}`} title={deal.ownerName}>
                    {deal.ownerName || ""}
                  </td>
                  <td
                    className="relative border-b border-gray-200 px-1 py-1.5 text-center"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="inline-flex items-center justify-center text-gray-500">
                      <RowActions
                        mode={mode}
                        onDelete={() => onDeleteDeal?.(deal)}
                        onArchive={() => onArchiveDeal?.(deal)}
                        onUnarchive={() => onUnarchiveDeal?.(deal)}
                      />
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════
function Deals() {
  const navigate = useNavigate();
  const [deals, setDeals] = useState([]);
  const [columns, setColumns] = useState(FALLBACK_COLUMNS);
  const [pipelines, setPipelines] = useState([]);
  const [selectedPipelineId, setSelectedPipelineId] = useState(null);

  const [activeView, setActiveView] = useState("kanban");
  const [isAddDealOpen, setIsAddDealOpen] = useState(false);
  const [openedTick, setOpenedTick] = useState(0);
  const openedIds = useMemo(() => new Set(getOpenedLeadIds()), [openedTick, deals]);

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [notConnected, setNotConnected] = useState(false);

  const [showClosed, setShowClosed] = useState(false);
  const [sortBy, setSortBy] = useState("next_activity");
  const [sortAsc, setSortAsc] = useState(true);

  // Filter / Add-condition search / Archived
  const [conditionSearch, setConditionSearch] = useState("");
  const [activeFilter, setActiveFilter] = useState(null);
  const [archivedDeals, setArchivedDeals] = useState([]);
  const [archivedLoading, setArchivedLoading] = useState(false);

  const [draggingDealId, setDraggingDealId] = useState(null);
  const [dragOverStageId, setDragOverStageId] = useState(null);
  const [movingDealIds, setMovingDealIds] = useState(() => new Set());
  const dragFromStageRef = useRef(null);
  const didDragRef = useRef(false);

  const asList = (res) => {
    const body = res?.data;
    if (Array.isArray(body?.data)) return body.data;
    if (Array.isArray(body)) return body;
    return [];
  };

  const pickDefaultPipelineId = (pipelineList) => {
    if (!pipelineList?.length) return null;
    const active = pipelineList.find((p) => p.active !== false && p.selected !== false);
    return active?.id ?? pipelineList[0].id;
  };

  const loadStagesForPipeline = useCallback(async (pipelineId) => {
    if (!pipelineId) {
      setColumns(FALLBACK_COLUMNS);
      return FALLBACK_COLUMNS;
    }
    const sRes = await apiGet(apiPath.pipedriveStages, { pipeline_id: pipelineId });
    let stageRows = asList(sRes).filter((s) => String(s.pipeline_id) === String(pipelineId));
    if (!stageRows.length) stageRows = asList(sRes);
    const stages = stageRows.map(mapStageFromApi).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    setColumns(stages.length ? stages : FALLBACK_COLUMNS);
    return stages;
  }, []);

  const loadDeals = useCallback(async (pipelineId, { includeClosed = false } = {}) => {
    if (!pipelineId) { setDeals([]); return; }
    const params = { pipeline_id: pipelineId, limit: 500 };
    if (!includeClosed) params.status = "open";
    const dealsRes = await apiGet(apiPath.pipedriveDeals, params);
    setDeals(asList(dealsRes).map(mapDealFromApi));
  }, []);

  const loadPipelineBoard = useCallback(
    async (pipelineId, opts = {}) => {
      await loadStagesForPipeline(pipelineId);
      await loadDeals(pipelineId, opts);
    },
    [loadStagesForPipeline, loadDeals]
  );

  const loadAll = useCallback(
    async (preferredPipelineId = null, opts = {}) => {
      try {
        setError(null);
        setNotConnected(false);

        const statusRes = await apiGet(apiPath.pipedriveStatus, {});
        if (statusRes?.data?.data?.connected !== true) {
          setNotConnected(true);
          setPipelines([]);
          setDeals([]);
          setColumns(FALLBACK_COLUMNS);
          setSelectedPipelineId(null);
          return;
        }

        const pRes = await apiGet(apiPath.pipedrivePipelines, {});
        const pipelineList = asList(pRes);
        setPipelines(pipelineList);

        const pipelineId =
          preferredPipelineId ||
          selectedPipelineId ||
          pickDefaultPipelineId(pipelineList);

        if (!pipelineId) {
          setColumns(FALLBACK_COLUMNS);
          setDeals([]);
          return;
        }

        setSelectedPipelineId(pipelineId);
        await loadPipelineBoard(pipelineId, opts);
      } catch (e) {
        const status = e?.response?.status || e?.status;
        const body = e?.response?.data || {};
        const code = body?.code || e?.code;
        const errText = String(body?.error || "");

        const isPipedriveAuth =
          status === 401 ||
          status === 403 ||
          code === "PIPEDRIVE_NOT_CONNECTED" ||
          errText.includes("Scope and URL mismatch");

        if (isPipedriveAuth) {
          setNotConnected(true);
          setPipelines([]);
          setDeals([]);
          setColumns(FALLBACK_COLUMNS);
          setSelectedPipelineId(null);
        } else {
          setError(body?.message || e?.message || "Failed to load deals");
        }
      }
    },
    [loadPipelineBoard, selectedPipelineId]
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      await loadAll(null, { includeClosed: showClosed });
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSelectPipeline = async (id) => {
    if (id == null) return;
    setSelectedPipelineId(id);
    setLoading(true);
    setError(null);
    try {
      await loadPipelineBoard(id, { includeClosed: showClosed });
    } catch (e) {
      setError(e?.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  };

  const filteredConditions = useMemo(() => {
    const q = conditionSearch.trim().toLowerCase();
    if (!q) return FILTER_CONDITIONS;
    return FILTER_CONDITIONS.filter((c) => c.label.toLowerCase().includes(q));
  }, [conditionSearch]);

  // FILTER (uses GET /v1/pipedrive/deals/filter)
  const runFilter = useCallback(
    async (params) => {
      setLoading(true);
      setError(null);
      try {
        const baseParams = {
          limit: 500,
          pipeline_id: selectedPipelineId || undefined,
          ...params,
        };
        // Don't force status=open when a preset/condition already defines scope
        if (!baseParams.preset && !baseParams.condition && !baseParams.status && !showClosed) {
          baseParams.status = "open";
        }
        const res = await apiGet(apiPath.pipedriveFilter, baseParams);
        setDeals(asList(res).map(mapDealFromApi));
      } catch (e) {
        setError(e?.response?.data?.message || e.message || "Filter failed");
      } finally {
        setLoading(false);
      }
    },
    [showClosed, selectedPipelineId]
  );

  const applyFilter = (filterObj) => {
    setConditionSearch("");
    setActiveFilter(filterObj);
    runFilter(filterObj.params);
  };

  const clearFilter = () => {
    setActiveFilter(null);
    if (selectedPipelineId) {
      loadDeals(selectedPipelineId, { includeClosed: showClosed });
    }
  };

  // ARCHIVED VIEW — GET /deals/archived/summary → Pipedrive /deals/archived
  const loadArchivedDeals = useCallback(async () => {
    setArchivedLoading(true);
    setError(null);
    try {
      // Do not pass pipeline_id — Archive should list all archived deals like Pipedrive
      const res = await apiGet(apiPath.pipedriveArchived, { limit: 500 });
      const body = res?.data;
      const payload = body?.data ?? body ?? {};
      const rows = Array.isArray(payload.deals)
        ? payload.deals
        : Array.isArray(payload)
          ? payload
          : Array.isArray(payload.data)
            ? payload.data
            : [];
      setArchivedDeals(rows.map(mapDealFromApi));
      if (!rows.length) {
        console.warn("[Archive] API returned 0 deals. meta:", payload.meta || body?.meta);
      }
    } catch (e) {
      setError(e?.response?.data?.message || e.message || "Could not load archived deals");
      setArchivedDeals([]);
    } finally {
      setArchivedLoading(false);
    }
  }, []);

  const switchView = async (view) => {
    setActiveView(view);
    setConditionSearch("");
    if (view === "archive") {
      await loadArchivedDeals();
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      if (activeView === "archive") await loadArchivedDeals();
      else if (activeFilter) await runFilter(activeFilter.params);
      else if (selectedPipelineId) await loadPipelineBoard(selectedPipelineId, { includeClosed: showClosed });
      else await loadAll(null, { includeClosed: showClosed });
    } finally {
      setRefreshing(false);
    }
  };

  const handleToggleClosed = async () => {
    const next = !showClosed;
    setShowClosed(next);
    if (selectedPipelineId) {
      setLoading(true);
      try { await loadDeals(selectedPipelineId, { includeClosed: next }); }
      finally { setLoading(false); }
    }
  };

  const handleDealSaved = async (created) => {
    try {
      if (created?.id) {
        setDeals((prev) => {
          if (prev.some((d) => String(d.id) === String(created.id))) return prev;
          return [...prev, mapDealFromApi(created)];
        });
      }
      // Always reload so board/list match Pipedrive (avoids stale 304 cache feel)
      if (selectedPipelineId) {
        await loadDeals(selectedPipelineId, { includeClosed: showClosed });
      }
      setIsAddDealOpen(false);
    } catch (e) {
      console.error("Refresh after create deal failed:", e);
      if (selectedPipelineId) await loadDeals(selectedPipelineId, { includeClosed: showClosed });
      setIsAddDealOpen(false);
    }
  };

  const handleDeleteDeal = async (deal) => {
    if (!window.confirm(`Delete deal "${deal.title}"?`)) return;
    try {
      await apiDelete(`${apiPath.pipedriveDeals}/${deal.id}`);
      setDeals((prev) => prev.filter((d) => d.id !== deal.id));
    } catch (e) {
      alert(`Could not delete deal: ${e?.response?.data?.message || e.message}`);
    }
  };

  const handleArchiveDeal = async (deal) => {
    if (!window.confirm(`Archive deal "${deal.title}"?`)) return;
    try {
      // Pipedrive archive flag (not delete) — so it appears under Archive view
      await apiPatch(`${apiPath.pipedriveDeals}/${deal.id}`, { is_archived: true });
      setDeals((prev) => prev.filter((d) => d.id !== deal.id));
      if (activeView === "archive") await loadArchivedDeals();
    } catch (e) {
      alert(`Could not archive deal: ${e?.response?.data?.message || e.message}`);
    }
  };

  const handleUnarchiveDeal = async (deal) => {
    if (!window.confirm(`Unarchive deal "${deal.title}"?`)) return;
    try {
      await apiPatch(`${apiPath.pipedriveDeals}/${deal.id}`, { is_archived: false });
      setArchivedDeals((prev) => prev.filter((d) => d.id !== deal.id));
      if (selectedPipelineId) {
        await loadDeals(selectedPipelineId, { includeClosed: showClosed });
      }
    } catch (e) {
      alert(`Could not unarchive deal: ${e?.response?.data?.message || e.message}`);
    }
  };

  const handleDragStart = (e, deal) => {
    if (movingDealIds.has(deal.id)) { e.preventDefault(); return; }
    didDragRef.current = true;
    setDraggingDealId(deal.id);
    dragFromStageRef.current = deal.stageId;
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", deal.id);
    requestAnimationFrame(() => {
      if (e.currentTarget) e.currentTarget.style.opacity = "0.45";
    });
  };

  const handleDragEnd = (e) => {
    if (e.currentTarget) e.currentTarget.style.opacity = "";
    setDraggingDealId(null);
    setDragOverStageId(null);
    dragFromStageRef.current = null;
    setTimeout(() => { didDragRef.current = false; }, 0);
  };

  const handleColumnDragOver = (e, stageId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    if (dragOverStageId !== stageId) setDragOverStageId(stageId);
  };

  const handleColumnDragLeave = (e, stageId) => {
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverStageId((cur) => (cur === stageId ? null : cur));
    }
  };

  const moveDealToStage = async (dealId, toStageId) => {
    const fromStageId = dragFromStageRef.current;
    if (!dealId || !toStageId || String(fromStageId) === String(toStageId)) return;
    if (movingDealIds.has(dealId)) return;

    const prevDeals = deals;
    setDeals((list) =>
      list.map((d) => (d.id === dealId ? { ...d, stageId: String(toStageId) } : d))
    );
    setMovingDealIds((s) => new Set(s).add(dealId));
    setDraggingDealId(null);
    setDragOverStageId(null);

    try {
      await apiPatch(`${apiPath.pipedriveDeals}/${dealId}`, { stage_id: Number(toStageId) });
    } catch (e) {
      setDeals(prevDeals);
      setError(e?.response?.data?.message || e?.message || "Could not move deal in Pipedrive");
    } finally {
      setMovingDealIds((s) => {
        const next = new Set(s);
        next.delete(dealId);
        return next;
      });
    }
  };

  const handleColumnDrop = async (e, toStageId) => {
    e.preventDefault();
    const dealId = e.dataTransfer.getData("text/plain") || draggingDealId;
    await moveDealToStage(dealId, toStageId);
  };

  const sortDealList = useCallback(
    (list) => {
      const arr = [...(list || [])];
      const dir = sortAsc ? 1 : -1;
      arr.sort((a, b) => {
        let cmp = 0;
        switch (sortBy) {
          case "title":
            cmp = cmpStr(a.title, b.title);
            break;
          case "value":
            cmp = (Number(a.value) || 0) - (Number(b.value) || 0);
            break;
          case "person":
            cmp = cmpStr(a.personName, b.personName);
            break;
          case "org":
            cmp = cmpStr(a.orgName, b.orgName);
            break;
          case "expected_close":
            cmp = toTime(a.expectedCloseDate) - toTime(b.expectedCloseDate);
            break;
          case "created":
            cmp = toTime(a.addTime) - toTime(b.addTime);
            break;
          case "updated":
            cmp = toTime(a.updateTime) - toTime(b.updateTime);
            break;
          case "done_activities":
            cmp = (a.doneActivities || 0) - (b.doneActivities || 0);
            break;
          case "activities_todo":
            cmp = (a.activitiesToDo || 0) - (b.activitiesToDo || 0);
            break;
          case "products":
            cmp = (a.productsCount || 0) - (b.productsCount || 0);
            break;
          case "owner":
            cmp = cmpStr(a.ownerName, b.ownerName);
            break;
          case "last_email_sent":
            cmp = toTime(a.lastEmailSent) - toTime(b.lastEmailSent);
            break;
          case "last_email_received":
            cmp = toTime(a.lastEmailReceived) - toTime(b.lastEmailReceived);
            break;
          case "next_activity":
          default: {
            const at = toTime(a.nextActivityDate);
            const bt = toTime(b.nextActivityDate);
            if (!at && !bt) cmp = toTime(b.addTime) - toTime(a.addTime);
            else if (!at) cmp = 1;
            else if (!bt) cmp = -1;
            else cmp = at - bt;
            break;
          }
        }
        return cmp * dir;
      });
      return arr;
    },
    [sortBy, sortAsc]
  );

  const sortedDeals = useMemo(() => sortDealList(deals), [deals, sortDealList]);
  const sortedArchivedDeals = useMemo(
    () => sortDealList(archivedDeals),
    [archivedDeals, sortDealList]
  );

  const visibleDeals = activeView === "archive" ? sortedArchivedDeals : sortedDeals;

  const dealsByStage = useMemo(() => {
    const grouped = columns.reduce((acc, col) => { acc[col.id] = []; return acc; }, {});
    visibleDeals.forEach((deal) => {
      if (grouped[deal.stageId]) grouped[deal.stageId].push(deal);
    });
    return grouped;
  }, [visibleDeals, columns]);

  const stageValue = (stageId) =>
    (dealsByStage[stageId] || []).reduce((sum, d) => sum + (Number(d.value) || 0), 0);

  const isOverdue = (d) => d.nextActivityDate && new Date(d.nextActivityDate) < new Date();

  const getRottingInfo = (d) => {
    const stage = columns.find((c) => String(c.id) === String(d.stageId));
    const days = daysSince(d.stageChangeTime || d.updateTime || d.addTime);
    if (days == null) return { days: null, isRotting: false };
    const threshold = stage?.daysToRotten > 0 ? stage.daysToRotten : 7;
    const rottingEnabled = stage?.rottenFlag !== false;
    return { days, isRotting: rottingEnabled && days >= threshold };
  };

  const openDealDetail = (deal) => {
    if (!deal?.id) return;
    markLeadOpened(deal.id);
    setOpenedTick((t) => t + 1);
    navigate(`/pipedrive/deal-pipeline/${deal.id}`);
  };

  useEffect(() => {
    const onFocus = () => setOpenedTick((t) => t + 1);
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, []);

  return (
    // OLD page: className="flex flex-col h-screen bg-white text-gray-800 font-sans overflow-hidden"
    <div className={`flex flex-col h-screen bg-white overflow-hidden ${F.page}`}>

      {/* TOP TOOLBAR */}
      <header className="flex items-center justify-between px-4 py-3 bg-white border-b border-gray-200">
        <div className="flex items-center space-x-3">
          <div className="flex bg-white border border-gray-300 rounded overflow-hidden">
            <button
              type="button"
              onClick={() => switchView("kanban")}
              className={`px-2 py-1.5 border-r border-gray-300 ${activeView === "kanban" ? "bg-blue-50 text-blue-600" : "text-gray-500 hover:bg-gray-50"}`}
              title="Pipeline"
            >
              <FiColumns size={18} />
            </button>
            <button
              type="button"
              onClick={() => switchView("list")}
              className={`px-2 py-1.5 border-r border-gray-300 ${activeView === "list" ? "bg-blue-50 text-blue-600" : "text-gray-500 hover:bg-gray-50"}`}
              title="List"
            >
              <FiList size={18} />
            </button>
            <button
              type="button"
              onClick={() => switchView("archive")}
              className={`px-2 py-1.5 ${activeView === "archive" ? "bg-blue-50 text-blue-600" : "text-gray-500 hover:bg-gray-50"}`}
              title="Archive"
            >
              <FiArchive size={18} />
            </button>
          </div>

          <div className="flex bg-[#317e3e] rounded overflow-hidden text-white shadow-sm">
            {/* OLD: className="px-3 py-1.5 text-sm font-semibold ..." */}
            <button onClick={() => setIsAddDealOpen(true)} className={`px-3 py-1.5 ${F.toolbar} font-semibold hover:bg-[#286834] flex items-center`}>
              <FiPlus className="mr-1" size={16} /> Deal
            </button>
          </div>
        </div>

        {/* RIGHT: count + refresh + Filter (aligned right like Pipedrive) */}
        {/* OLD: className="flex items-center space-x-3 text-sm" */}
        <div className={`flex items-center space-x-3 ${F.toolbar}`}>
          {/* OLD: className="flex items-center font-medium text-gray-700" */}
          <div className={`flex items-center ${F.toolbarMuted}`}>
            {activeView === "archive" ? `${visibleDeals.length} archived deals` : `${visibleDeals.length} deals`}
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className={`p-1.5 rounded ${refreshing ? "text-gray-300" : "text-gray-500 hover:bg-gray-100"}`}
            title="Refresh"
          >
            <FiRefreshCw size={16} className={refreshing ? "animate-spin" : ""} />
          </button>

          <Dropdown
            align="right"
            width="w-64"
            trigger={
              <div
                /* OLD: flex items-center text-sm px-2.5 ... */
                className={`flex items-center ${F.toolbar} px-2.5 py-1.5 border rounded ${
                  activeFilter
                    ? "border-blue-400 bg-blue-50 text-blue-600"
                    : "border-gray-300 text-gray-600 hover:bg-gray-50"
                }`}
              >
                <FiFilter className="mr-1.5" size={15} />
                {activeFilter ? activeFilter.label : "Filter"}
                <FiChevronDown className="ml-1.5" size={14} />
              </div>
            }
          >
            {FILTER_PRESETS.map((f) => (
              <DropdownItem
                key={f.key}
                active={activeFilter?.params?.preset === f.key}
                onClick={() => applyFilter({ label: f.label, params: f.params })}
              >
                {f.label}
              </DropdownItem>
            ))}
            <div className="border-t border-gray-100 my-1" />
            <DropdownItem onClick={clearFilter}>
              <span className="text-red-600 font-medium">Clear filter</span>
            </DropdownItem>
          </Dropdown>
        </div>
      </header>

      {/* SUB TOOLBAR — Add condition + Show closed */}
      <div className="flex items-center justify-between px-4 py-1.5 border-b border-gray-200 text-xs bg-white min-h-[34px]">
        <div className="flex items-center gap-3">
          <Dropdown
            align="left"
            width="w-[340px]"
            trigger={
              <div className="flex items-center gap-1.5 text-gray-600 hover:text-gray-900 cursor-pointer px-1.5 py-1 rounded hover:bg-gray-50">
                <FiFilter className="text-gray-500" size={14} />
                <span className="font-medium">Add condition</span>
                {/* <FiInfo className="text-gray-400" size={12} title="Suggested filters" /> */}
              </div>
            }
          >
            {/* Search inside Add condition (Pipedrive-style) */}
            <div className="px-3 pt-3 pb-2" onClick={(e) => e.stopPropagation()}>
              <div className="relative">
                <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                  <FiSearch size={15} />
                </span>
                <input
                  type="text"
                  value={conditionSearch}
                  onChange={(e) => setConditionSearch(e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                  onMouseDown={(e) => e.stopPropagation()}
                  placeholder="Search"
                  className="w-full pl-8 pr-3 py-2 text-sm border border-blue-400 rounded focus:outline-none focus:ring-1 focus:ring-blue-500 bg-white"
                />
              </div>
            </div>
            <div className="px-3 pb-1 text-[10px] font-semibold tracking-wide text-gray-400 uppercase flex items-center justify-between">
              Suggested
              <FiChevronUp size={14} />
            </div>
            <div className="px-2 pb-2 max-h-64 overflow-y-auto space-y-1">
              {filteredConditions.length === 0 ? (
                <div className="px-2.5 py-3 text-sm text-gray-400 text-center">No matching conditions</div>
              ) : (
                filteredConditions.map((c) => (
                  <button
                    key={c.key}
                    type="button"
                    onClick={() => applyFilter({ label: c.label, params: c.params })}
                    className="w-full text-left px-2.5 py-1.5 text-sm text-gray-800 bg-gray-50 hover:bg-gray-100 rounded-md"
                  >
                    {c.label}
                  </button>
                ))
              )}
            </div>
          </Dropdown>
          {activeFilter && (
            <button
              type="button"
              onClick={clearFilter}
              className="text-red-600 hover:underline font-medium"
            >
              Clear: {activeFilter.label}
            </button>
          )}
        </div>
        <div className="flex items-center space-x-3">
          <button
            type="button"
            onClick={handleToggleClosed}
            className="text-blue-600 hover:underline font-medium"
          >
            {showClosed ? "Hide closed deals" : "Show closed deals"}
          </button>
          <button
            type="button"
            onClick={() => setSortAsc((v) => !v)}
            className="p-1 rounded text-blue-600 hover:bg-blue-50"
            title={sortAsc ? "Ascending — click for descending" : "Descending — click for ascending"}
          >
            <FiArrowUp size={16} className={sortAsc ? "" : "rotate-180"} />
          </button>
          <Dropdown
            align="right"
            width="w-64"
            trigger={
              <div className="flex items-center text-blue-600 font-medium cursor-pointer">
                <span>Sort by: {sortLabel(sortBy)}</span>
                <FiChevronDown className="ml-1" size={14} />
              </div>
            }
          >
            <div className="px-4 pt-2 pb-1 text-[10px] font-semibold tracking-wide text-gray-400 uppercase">
              Sort by
            </div>
            {SORT_OPTIONS.map((opt) => (
              <DropdownItem
                key={opt.key}
                active={sortBy === opt.key}
                onClick={() => setSortBy(opt.key)}
              >
                {opt.label}
              </DropdownItem>
            ))}
          </Dropdown>
        </div>
      </div>

      {/* BANNERS */}
      {notConnected && (
        <div className="bg-yellow-50 border-b border-yellow-200 px-4 py-2 text-sm text-yellow-800 flex items-center justify-between">
          <span>Pipedrive is not connected. Connect it to view deals.</span>
          <a href="/pipedrive-connection" className="font-semibold underline">Go to Integration</a>
        </div>
      )}
      {error && !notConnected && (
        <div className="bg-red-50 border-b border-red-200 px-4 py-2 text-sm text-red-700 flex items-center justify-between">
          <span>Error: {error}</span>
          <button onClick={handleRefresh} className="font-semibold underline">Retry</button>
        </div>
      )}

      {/* MAIN CONTENT */}
      <div className="flex-1 overflow-hidden bg-white relative">

        {/* KANBAN */}
        {activeView === "kanban" && (
          <div className="h-full overflow-x-auto no-scrollbar bg-[#f7f7f7] p-4">
            <div className="flex h-full gap-3 items-start">
              {columns.map((column) => {
                const isDropTarget = dragOverStageId === column.id && draggingDealId;
                return (
                  <div
                    key={column.id}
                    className={`flex-shrink-0 ${KANBAN_COLUMN_WIDTH} rounded flex flex-col h-full max-h-full transition-colors ${
                      isDropTarget ? "bg-[#dce8ff] ring-2 ring-blue-400 ring-inset" : "bg-[#ebebeb]"
                    }`}
                    onDragOver={(e) => handleColumnDragOver(e, column.id)}
                    onDragLeave={(e) => handleColumnDragLeave(e, column.id)}
                    onDrop={(e) => handleColumnDrop(e, column.id)}
                  >
                    <div className="px-3 pt-3 pb-2">
                      {/* OLD: className="font-semibold text-gray-800 text-sm truncate" */}
                      <h2 className={`${F.colTitle} truncate`} title={column.title}>
                        {column.title}
                      </h2>
                      {/* OLD: className="text-xs text-gray-500 mt-1" */}
                      <p className={`${F.colMeta} mt-1`}>
                        ${stageValue(column.id)} · {(dealsByStage[column.id] || []).length}{" "}
                        {(dealsByStage[column.id] || []).length === 1 ? "deal" : "deals"}
                      </p>
                    </div>
                    <div className="flex-1 px-2 pb-2 space-y-2 overflow-y-auto no-scrollbar min-h-[80px]">
                      {(dealsByStage[column.id] || []).length === 0 ? (
                        <div className="flex items-center justify-center pt-6 pb-4 text-gray-400">
                          {isDropTarget ? (
                            <span className="text-xs text-blue-600 font-medium">Drop here</span>
                          ) : (
                            <button type="button" onClick={() => setIsAddDealOpen(true)}>
                              <FiPlus size={18} />
                            </button>
                          )}
                        </div>
                      ) : (
                        dealsByStage[column.id].map((deal) => {
                          const isMoving = movingDealIds.has(deal.id);
                          const isDragging = draggingDealId === deal.id;
                          const { days: rottingDays, isRotting } = getRottingInfo(deal);
                          const noActivity = !deal.hasNextActivity;
                          const showWarn = noActivity || isOverdue(deal);
                          return (
                            <div
                              key={deal.id}
                              draggable={!isMoving}
                              onDragStart={(e) => handleDragStart(e, deal)}
                              onDragEnd={handleDragEnd}
                              onClick={() => {
                                if (didDragRef.current || isDragging || isMoving) return;
                                openDealDetail(deal);
                              }}
                              className={`w-full box-border p-3 rounded border select-none transition-shadow ${
                                isRotting ? "bg-[#fce8e8] border-[#f0c4c4]" : "bg-white border-gray-200 shadow-sm"
                              } ${
                                isMoving ? "opacity-60 cursor-wait" : isDragging ? "cursor-grabbing shadow-md" : "cursor-pointer hover:shadow-md"
                              }`}
                            >
                              {/* OLD: text-[13px] leading-snug font-semibold ... text-[#21232c] / rotten text-[#8b3a3a] */}
                              <p className={`line-clamp-2 break-words ${isRotting ? F.cardTitleRotten : F.cardTitle}`}>
                                {deal.title}
                              </p>
                              {deal.personName ? (
                                /* OLD: text-xs mt-0.5 truncate text-gray-500 / rotten text-[#a85a5a] */
                                <p className={`mt-0.5 truncate ${isRotting ? F.cardSubRotten : F.cardSub}`}>
                                  {deal.personName}
                                </p>
                              ) : null}

                              <div className="flex items-center justify-between gap-2 mt-2.5 min-h-[18px]">
                                {/* OLD: text-xs text-gray-500 */}
                                <div className={`flex items-center gap-1.5 min-w-0 ${F.cardMeta}`}>
                                  <FiUser size={14} className="text-gray-400 shrink-0" />
                                  <span className="truncate">
                                    {deal.currency || "USD"} {Number(deal.value) || 0}
                                  </span>
                                  {isMoving && <span className="text-gray-400 shrink-0">Saving…</span>}
                                </div>
                                <div className="flex items-center gap-1.5 shrink-0">
                                  {isRotting && (
                                    <span title={`Rotting for ${rottingDays} days`}
                                      /* OLD: ... text-white text-[10px] font-bold ... */
                                      className={`inline-flex items-center justify-center px-1.5 h-[18px] rounded-full bg-[#c62828] leading-none ${F.badge}`}>
                                      {rottingDays}d
                                    </span>
                                  )}
                                  {showWarn && (
                                    <span title={noActivity ? "No activity" : "Overdue"}>
                                      <FiAlertTriangle size={14} className="text-amber-500" />
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* LIST */}
        {activeView === "list" && (
          <ListView
            mode="list"
            deals={visibleDeals}
            onAddDeal={() => setIsAddDealOpen(true)}
            onImportDeals={() => alert("Import deals clicked")}
            onDeleteDeal={handleDeleteDeal}
            onArchiveDeal={handleArchiveDeal}
            onOpenDeal={openDealDetail}
            openedIds={openedIds}
          />
        )}

        {/* ARCHIVE — same table as list (no "Loading archived…" banner) */}
        {activeView === "archive" && (
          <ListView
            mode="archive"
            deals={sortedArchivedDeals}
            onAddDeal={() => setIsAddDealOpen(true)}
            onImportDeals={() => alert("Import deals clicked")}
            onDeleteDeal={handleDeleteDeal}
            onArchiveDeal={handleArchiveDeal}
            onUnarchiveDeal={handleUnarchiveDeal}
            onOpenDeal={openDealDetail}
            openedIds={openedIds}
          />
        )}
        {/* archivedLoading spinner removed — was: Loading archived deals… */}
      </div>

      {/* ADD DEAL MODAL */}
      {isAddDealOpen && (
        <AddDeal
          pipelineId={selectedPipelineId}
          onClose={() => setIsAddDealOpen(false)}
          onSave={handleDealSaved}
        />
      )}

      {/* Hide scrollbars (horizontal + vertical) across deals views */}
      <style>{`
        .no-scrollbar::-webkit-scrollbar { display: none; width: 0; height: 0; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>
    </div>
  );
}

export default Deals;









// // src/pages/pipedrive/Deals.js
// import React, { useState, useMemo, useRef, useEffect, useCallback } from "react";
// import { useNavigate } from "react-router-dom";
// import { apiGet, apiPost, apiPatch , apiDelete } from "../../utils/apiFetch";
// import apiPath from "../../utils/pathObj";
// import AddDeal from "./AddDeal";
// import { daysSince, getOpenedLeadIds, isLeadOpened, markLeadOpened } from "./openedLeads";
// import { resolveOrgName, resolveOwnerName, resolvePersonName } from "./pipedriveHelpers";

// // ═══════════════════════════════════════════════════════════════
// // MAPPERS
// // ═══════════════════════════════════════════════════════════════
// const mapDealFromApi = (d) => ({
//   id: String(d.id),
//   title: d.title || "(Untitled deal)",
//   value: d.value ?? 0,
//   currency: d.currency || "USD",
//   stageId: String(d.stage_id),
//   pipelineId: d.pipeline_id,
//   owner: d.owner_id,
//   ownerName: resolveOwnerName(d),
//   status: d.status || "open",
//   orgName: resolveOrgName(d),
//   personName: resolvePersonName(d),
//   expectedCloseDate: d.expected_close_date || "",
//   nextActivityDate: d.next_activity_date || "",
//   addTime: d.add_time || d.created_at || "",
//   updateTime: d.update_time || d.updated_at || "",
//   stageChangeTime: d.stage_change_time || d.update_time || d.add_time || "",
//   hasNextActivity: Boolean(d.next_activity_id || d.next_activity_date),
//   raw: d,
// });

// const mapStageFromApi = (s) => ({
//   id: String(s.id),
//   title: s.name,
//   pipelineId: s.pipeline_id,
//   order: s.order_nr,
//   daysToRotten: s.days_to_rotten > 0 ? Number(s.days_to_rotten) : 7,
//   rottenFlag: s.rotten_flag !== false,
// });

// const FALLBACK_COLUMNS = [
//   { id: "qualified", title: "Qualified" },
//   { id: "contact_made", title: "Contact Made" },
//   { id: "prospect_qualified", title: "Prospect Qualified" },
//   { id: "needs_defined", title: "Needs Defined" },
// ];

// // ═══════════════════════════════════════════════════════════════
// // ICONS
// // ═══════════════════════════════════════════════════════════════
// const PipelineIcon = ({ className = "" }) => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><path d="M4 7h16"/><path d="M7 12h10"/><path d="M10 17h4"/></svg>;
// const ListIcon = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="8" y1="6" x2="21" y2="6"></line><line x1="8" y1="12" x2="21" y2="12"></line><line x1="8" y1="18" x2="21" y2="18"></line><line x1="3" y1="6" x2="3.01" y2="6"></line><line x1="3" y1="12" x2="3.01" y2="12"></line><line x1="3" y1="18" x2="3.01" y2="18"></line></svg>;
// const PlusIcon = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>;
// const ChevronDownIcon = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>;
// const ChevronUpIcon = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="18 15 12 9 6 15"></polyline></svg>;
// const RefreshIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="23 4 23 10 17 10"></polyline><polyline points="1 20 1 14 7 14"></polyline><path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"></path></svg>;
// const FilterIcon = ({ className = "" }) => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon></svg>;
// const ThreeDotsIcon = () => <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="1"></circle><circle cx="19" cy="12" r="1"></circle><circle cx="5" cy="12" r="1"></circle></svg>;
// const ArrowUpIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="19" x2="12" y2="5"></line><polyline points="5 12 12 5 19 12"></polyline></svg>;
// const DownloadIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path><polyline points="7 10 12 15 17 10"></polyline><line x1="12" y1="15" x2="12" y2="3"></line></svg>;
// const CheckIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"></polyline></svg>;
// const SearchIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"></circle><line x1="21" y1="21" x2="16.65" y2="16.65"></line></svg>;
// const TargetIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><circle cx="12" cy="12" r="6"></circle><circle cx="12" cy="12" r="2"></circle></svg>;
// const CalendarMiniIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect><line x1="16" y1="2" x2="16" y2="6"></line><line x1="8" y1="2" x2="8" y2="6"></line><line x1="3" y1="10" x2="21" y2="10"></line></svg>;
// const HashIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="4" y1="9" x2="20" y2="9"></line><line x1="4" y1="15" x2="20" y2="15"></line><line x1="10" y1="3" x2="8" y2="21"></line><line x1="16" y1="3" x2="14" y2="21"></line></svg>;
// const BuildingIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="4" y="2" width="16" height="20" rx="2" ry="2"></rect><path d="M9 22v-4h6v4"></path><path d="M8 6h.01"></path><path d="M16 6h.01"></path><path d="M12 6h.01"></path><path d="M12 10h.01"></path><path d="M12 14h.01"></path><path d="M16 10h.01"></path><path d="M16 14h.01"></path><path d="M8 10h.01"></path><path d="M8 14h.01"></path></svg>;
// const UserIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>;
// const PersonMiniIcon = () => (
//   <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400 shrink-0">
//     <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
//     <circle cx="12" cy="7" r="4" />
//   </svg>
// );
// const BoxIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"></path><polyline points="3.27 6.96 12 12.01 20.73 6.96"></polyline><line x1="12" y1="22.08" x2="12" y2="12"></line></svg>;
// const WarnIcon = () => <svg width="14" height="14" viewBox="0 0 24 24" fill="#f59e0b" stroke="#f59e0b" strokeWidth="1"><path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13" stroke="#fff"/><circle cx="12" cy="17" r="0.6" fill="#fff"/></svg>;

// /** Pipedrive-like fixed column width so every deal card is the same size */
// const KANBAN_COLUMN_WIDTH = "w-[240px]";
// const SpinnerIcon = () => (
//   <svg className="animate-spin" width="18" height="18" viewBox="0 0 24 24" fill="none">
//     <circle cx="12" cy="12" r="10" stroke="#9ca3af" strokeWidth="3" opacity="0.25" />
//     <path d="M12 2a10 10 0 0 1 10 10" stroke="#317e3e" strokeWidth="3" strokeLinecap="round" />
//   </svg>
// );

// // ═══════════════════════════════════════════════════════════════
// // DROPDOWN
// // ═══════════════════════════════════════════════════════════════
// const Dropdown = ({ trigger, children, align = "left", width = "w-48" }) => {
//   const [isOpen, setIsOpen] = useState(false);
//   const dropdownRef = useRef(null);
//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (dropdownRef.current && !dropdownRef.current.contains(event.target)) setIsOpen(false);
//     };
//     document.addEventListener("mousedown", handleClickOutside);
//     return () => document.removeEventListener("mousedown", handleClickOutside);
//   }, []);
//   return (
//     <div className="relative inline-block text-left" ref={dropdownRef}>
//       <div onClick={() => setIsOpen(!isOpen)} className="cursor-pointer h-full">{trigger}</div>
//       {isOpen && (
//         <div className={`absolute mt-1 ${width} rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-50 ${align === "right" ? "right-0" : "left-0"}`}>
//           <div className="py-1" role="menu">{children}</div>
//         </div>
//       )}
//     </div>
//   );
// };

// const DropdownItem = ({ children, onClick }) => (
//   <button onClick={onClick} className="w-full text-left block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">{children}</button>
// );

// // ═══════════════════════════════════════════════════════════════
// // ROW ACTIONS
// // ═══════════════════════════════════════════════════════════════
// const RowActions = ({ onDelete, onArchive }) => {
//   const [open, setOpen] = useState(false);
//   const ref = useRef(null);
//   useEffect(() => {
//     const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
//     document.addEventListener("mousedown", h);
//     return () => document.removeEventListener("mousedown", h);
//   }, []);
//   return (
//     <div className="relative" ref={ref}>
//       <button onClick={(e) => { e.stopPropagation(); setOpen(!open); }}
//         className="p-1 rounded hover:bg-gray-100 text-gray-400">
//         <ThreeDotsIcon />
//       </button>
//       {open && (
//         <div className="absolute right-0 top-full mt-1 w-32 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 z-50 py-1">
//           <button onClick={(e) => { e.stopPropagation(); setOpen(false); onDelete?.(); }}
//             className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100">Delete</button>
//           <button onClick={(e) => { e.stopPropagation(); setOpen(false); onArchive?.(); }}
//             className="w-full text-left px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100">Archive</button>
//         </div>
//       )}
//     </div>
//   );
// };

// // ═══════════════════════════════════════════════════════════════
// // HELPERS
// // ═══════════════════════════════════════════════════════════════
// const formatMoney = (value, currency = "USD") => {
//   const num = Number(value) || 0;
//   try {
//     return new Intl.NumberFormat("en-US", { style: "currency", currency, maximumFractionDigits: 0 }).format(num);
//   } catch { return `$${num.toLocaleString()}`; }
// };
// const formatDate = (d) => {
//   if (!d) return "";
//   try {
//     const dt = new Date(d);
//     if (isNaN(dt.getTime())) return d;
//     return dt.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
//   } catch { return d; }
// };

// // ═══════════════════════════════════════════════════════════════
// // LIST VIEW
// // ═══════════════════════════════════════════════════════════════
// const ListView = ({ deals, onAddDeal, onImportDeals, onDeleteDeal, onArchiveDeal, onOpenDeal, openedIds }) => {
//   if (!deals.length) {
//     return (
//       <div className="flex-1 flex flex-col items-center justify-center h-full bg-white">
//         <h1 className="text-2xl font-semibold text-gray-800">Add your first deal</h1>
//         <p className="text-gray-500 mt-2 mb-8">Track your deals and manage your pipeline to close more business.</p>
//         <div className="flex space-x-3 mb-16">
//           <button onClick={onAddDeal} className="px-4 py-2 text-sm font-medium border border-gray-300 rounded hover:bg-gray-50 flex items-center shadow-sm text-gray-700">
//             <PlusIcon className="mr-1.5" /> Deal
//           </button>
//           <button onClick={onImportDeals} className="px-4 py-2 text-sm font-medium border border-gray-300 rounded hover:bg-gray-50 flex items-center shadow-sm text-gray-700">
//             <DownloadIcon className="mr-1.5" /> Import deals
//           </button>
//         </div>
//       </div>
//     );
//   }

//   return (
//     <div className="h-full overflow-auto no-scrollbar bg-white">
//       {/* <table className="w-full text-sm border-collapse table-fixed">
//         <colgroup>
//           <col style={{ width: "36px" }} />
//           <col style={{ width: "26%" }} />
//           <col style={{ width: "90px" }} />
//           <col style={{ width: "18%" }} />
//           <col style={{ width: "20%" }} />
//           <col style={{ width: "130px" }} />
//           <col style={{ width: "130px" }} />
//           <col style={{ width: "110px" }} />
//           <col style={{ width: "44px" }} />
//         </colgroup>
//         <thead className="sticky top-0 bg-white z-10">
//           <tr className="border-b border-gray-200 text-left text-xs font-semibold text-gray-600">
//             <th className="px-3 py-2.5"><input type="checkbox" className="rounded border-gray-300" /></th>
//             <th className="px-3 py-2.5">Title</th>
//             <th className="px-3 py-2.5">Value</th>
//             <th className="px-3 py-2.5">Organization</th>
//             <th className="px-3 py-2.5">Contact person</th>
//             <th className="px-3 py-2.5">Expected close date</th>
//             <th className="px-3 py-2.5">Next activity date</th>
//             <th className="px-3 py-2.5">Owner</th>
//             <th className="px-3 py-2.5"></th>
//           </tr>
//         </thead>
//         <tbody>
//           {deals.map((deal) => (
//             <tr key={deal.id} className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer">
//               <td className="px-3 py-2.5">
//                 <input type="checkbox" className="rounded border-gray-300" onClick={(e) => e.stopPropagation()} />
//               </td>
//               <td className="px-3 py-2.5 text-gray-800 font-medium truncate" title={deal.title}>{deal.title}</td>
//               <td className="px-3 py-2.5 text-gray-700 whitespace-nowrap">{formatMoney(deal.value, deal.currency)}</td>
//               <td className="px-3 py-2.5 text-gray-700 truncate" title={deal.orgName}>
//                 {deal.orgName || <span className="text-gray-300">—</span>}
//               </td>
//               <td className="px-3 py-2.5 text-gray-700 truncate" title={deal.personName}>
//                 {deal.personName || <span className="text-gray-300">—</span>}
//               </td>
//               <td className="px-3 py-2.5 text-gray-700 whitespace-nowrap">
//                 {deal.expectedCloseDate ? formatDate(deal.expectedCloseDate) : <span className="text-gray-300">—</span>}
//               </td>
//               <td className="px-3 py-2.5 text-gray-700 whitespace-nowrap">
//                 {deal.nextActivityDate ? formatDate(deal.nextActivityDate) : <span className="text-gray-300">—</span>}
//               </td>
//               <td className="px-3 py-2.5 text-gray-700 truncate" title={deal.ownerName}>
//                 {deal.ownerName || <span className="text-gray-300">—</span>}
//               </td>
//               <td className="px-2 py-2.5">
//                 <RowActions
//                   onDelete={() => onDeleteDeal?.(deal)}
//                   onArchive={() => onArchiveDeal?.(deal)}
//                 />
//               </td>
//             </tr>
//           ))}
//         </tbody>
//       </table> */}
    
// <div className="w-full overflow-x-auto rounded-sm border border-gray-200 bg-white">
//   <table className="w-full min-w-[1450px] table-fixed border-collapse text-center">
//     <colgroup>
//       <col style={{ width: "36px" }} />
//       <col style={{ width: "312px" }} />
//       <col style={{ width: "312px" }} />
//       <col style={{ width: "312px" }} />
//       <col style={{ width: "312px" }} />
//       <col style={{ width: "312px" }} />
//       <col style={{ width: "187px" }} />
//       <col style={{ width: "150px" }} />
//       <col style={{ width: "42px" }} />
//     </colgroup>

//     <thead className="sticky top-0 bg-white">
//       <tr className="text-left text-[14px] font-semibold text-[#292d38]">
//         <th className="border-b border-r border-gray-200 px-2 py-2">
//           <input
//             type="checkbox"
//             className="h-5 w-5 cursor-pointer rounded border-gray-300 accent-blue-600"
//             onClick={(e) => e.stopPropagation()}
//           />
//         </th>

//         <th className="border-b border-r border-gray-200 px-2.5 py-2">
//           Title
//         </th>

//         <th className="border-b border-r border-gray-200 px-2.5 py-2">
//           Value
//         </th>

//         <th className="border-b border-r border-gray-200 px-2.5 py-2">
//           Organization
//         </th>

//         <th className="border-b border-r border-gray-200 px-2.5 py-2">
//           Contact person
//         </th>

//         <th className="border-b border-r border-gray-200 px-2.5 py-2">
//           Expected close date
//         </th>

//         <th className="border-b border-r border-gray-200 px-2.5 py-2">
//           Next activity date
//         </th>

//         <th className="border-b border-r border-gray-200 px-2.5 py-2">
//           Owner
//         </th>

//         <th className="border-b border-gray-200 px-2 py-2 text-center">
//           <button
//             type="button"
//             title="Table settings"
//             className="inline-flex items-center justify-center text-gray-500 hover:text-gray-800"
//           >
//             {/* Settings icon */}
//             <svg
//               xmlns="http://www.w3.org/2000/svg"
//               width="18"
//               height="18"
//               viewBox="0 0 24 24"
//               fill="none"
//               stroke="currentColor"
//               strokeWidth="1.8"
//               strokeLinecap="round"
//               strokeLinejoin="round"
//             >
//               <circle cx="12" cy="12" r="3" />
//               <path d="m19.4 15 .1.1a1.7 1.7 0 0 1-2.4 2.4l-.1-.1a1.7 1.7 0 0 0-2.9 1.2v.2a1.7 1.7 0 0 1-3.4 0v-.2a1.7 1.7 0 0 0-2.9-1.2l-.1.1a1.7 1.7 0 0 1-2.4-2.4l.1-.1a1.7 1.7 0 0 0-1.2-2.9H4a1.7 1.7 0 0 1 0-3.4h.2a1.7 1.7 0 0 0 1.2-2.9l-.1-.1a1.7 1.7 0 0 1 2.4-2.4l.1.1a1.7 1.7 0 0 0 2.9-1.2V2a1.7 1.7 0 0 1 3.4 0v.2a1.7 1.7 0 0 0 2.9 1.2l.1-.1a1.7 1.7 0 0 1 2.4 2.4l-.1.1a1.7 1.7 0 0 0 1.2 2.9h.2a1.7 1.7 0 0 1 0 3.4h-.2a1.7 1.7 0 0 0-1.2 2.9Z" />
//             </svg>
//           </button>
//         </th>
//       </tr>
//     </thead>

//     <tbody>
//       {deals.map((deal) => {
//         const opened = openedIds?.has?.(deal.id) || isLeadOpened(deal.id);
//         return (
//         <tr
//           key={deal.id}
//           onClick={() => onOpenDeal?.(deal)}
//           className="group h-[38px] cursor-pointer hover:bg-gray-50"
//         >
//           <td className="border-b border-r border-gray-200 px-2 py-1">
//             <input
//               type="checkbox"
//               className="h-5 w-5 cursor-pointer rounded border-gray-300 accent-blue-600"
//               onClick={(e) => e.stopPropagation()}
//             />
//           </td>

//           <td
//             className={`overflow-hidden text-ellipsis whitespace-nowrap border-b border-r border-gray-200 px-2.5 py-1 ${
//               opened ? "text-[#292d38] font-normal" : "text-[#c94a5a] font-semibold"
//             }`}
//             title={deal.title}
//           >
//             {deal.title || "—"}
//           </td>

//           <td className="whitespace-nowrap border-b border-r border-gray-200 px-2.5 py-1 text-right text-[#292d38]">
//             {formatMoney(deal.value, deal.currency)}
//           </td>

//           <td
//             className="overflow-hidden text-ellipsis whitespace-nowrap border-b border-r border-gray-200 px-2.5 py-1 text-[#292d38]"
//             title={deal.orgName}
//           >
//             {deal.orgName || ""}
//           </td>

//           <td
//             className="overflow-hidden text-ellipsis whitespace-nowrap border-b border-r border-gray-200 px-2.5 py-1 text-[#292d38]"
//             title={deal.personName}
//           >
//             {deal.personName || ""}
//           </td>

//           <td className="whitespace-nowrap border-b border-r border-gray-200 px-2.5 py-1 text-[#292d38]">
//             {deal.expectedCloseDate
//               ? formatDate(deal.expectedCloseDate)
//               : ""}
//           </td>

//           <td className="whitespace-nowrap border-b border-r border-gray-200 px-2.5 py-1 text-[#292d38]">
//             {deal.nextActivityDate
//               ? formatDate(deal.nextActivityDate)
//               : ""}
//           </td>

//           <td
//             className="overflow-hidden text-ellipsis whitespace-nowrap border-b border-r border-gray-200 px-2.5 py-1 text-[#292d38]"
//             title={deal.ownerName}
//           >
//             {deal.ownerName || ""}
//           </td>

//           <td className="border-b border-gray-200 px-1 py-1 text-center">
//             <div className="inline-flex items-center justify-center text-gray-500">
//               <RowActions
//                 onDelete={() => onDeleteDeal?.(deal)}
//                 onArchive={() => onArchiveDeal?.(deal)}
//               />
//             </div>
//           </td>
//         </tr>
//       );
//       })}
//     </tbody>
//   </table>
// </div>





//     </div>
//   );
// };

// // ═══════════════════════════════════════════════════════════════
// // MAIN COMPONENT
// // ═══════════════════════════════════════════════════════════════
// function Deals() {
//   const navigate = useNavigate();
//   const [deals, setDeals] = useState([]);
//   const [columns, setColumns] = useState(FALLBACK_COLUMNS);
//   const [pipelines, setPipelines] = useState([]);
//   const [selectedPipelineId, setSelectedPipelineId] = useState(null);

//   const [activeView, setActiveView] = useState("kanban");
//   const [isAddDealOpen, setIsAddDealOpen] = useState(false);
//   const [openedTick, setOpenedTick] = useState(0);
//   const openedIds = useMemo(() => new Set(getOpenedLeadIds()), [openedTick, deals]);

//   const [loading, setLoading] = useState(true);
//   const [refreshing, setRefreshing] = useState(false);
//   const [error, setError] = useState(null);
//   const [notConnected, setNotConnected] = useState(false);

//   const [showClosed, setShowClosed] = useState(false);
//   const [sortBy, setSortBy] = useState("next_activity");

//   const [draggingDealId, setDraggingDealId] = useState(null);
//   const [dragOverStageId, setDragOverStageId] = useState(null);
//   const [movingDealIds, setMovingDealIds] = useState(() => new Set());
//   const dragFromStageRef = useRef(null);
//   const didDragRef = useRef(false);

//   const asList = (res) => {
//     const body = res?.data;
//     if (Array.isArray(body?.data)) return body.data;
//     if (Array.isArray(body)) return body;
//     return [];
//   };

//   const pickDefaultPipelineId = (pipelineList) => {
//     if (!pipelineList?.length) return null;
//     const active = pipelineList.find((p) => p.active !== false && p.selected !== false);
//     return active?.id ?? pipelineList[0].id;
//   };

//   const loadStagesForPipeline = useCallback(async (pipelineId) => {
//     if (!pipelineId) {
//       setColumns(FALLBACK_COLUMNS);
//       return FALLBACK_COLUMNS;
//     }
//     const sRes = await apiGet(apiPath.pipedriveStages, { pipeline_id: pipelineId });
//     let stageRows = asList(sRes).filter((s) => String(s.pipeline_id) === String(pipelineId));
//     if (!stageRows.length) stageRows = asList(sRes);
//     const stages = stageRows.map(mapStageFromApi).sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
//     setColumns(stages.length ? stages : FALLBACK_COLUMNS);
//     return stages;
//   }, []);

//   const loadDeals = useCallback(async (pipelineId, { includeClosed = false } = {}) => {
//     if (!pipelineId) {
//       setDeals([]);
//       return;
//     }
//     const params = { pipeline_id: pipelineId, limit: 500 };
//     if (!includeClosed) params.status = "open";
//     const dealsRes = await apiGet(apiPath.pipedriveDeals, params);
//     setDeals(asList(dealsRes).map(mapDealFromApi));
//   }, []);

//   const loadPipelineBoard = useCallback(
//     async (pipelineId, opts = {}) => {
//       await loadStagesForPipeline(pipelineId);
//       await loadDeals(pipelineId, opts);
//     },
//     [loadStagesForPipeline, loadDeals]
//   );

//   const loadAll = useCallback(
//     async (preferredPipelineId = null, opts = {}) => {
//       try {
//         setError(null);
//         setNotConnected(false);

//         const statusRes = await apiGet(apiPath.pipedriveStatus, {});
//         if (statusRes?.data?.data?.connected !== true) {
//           setNotConnected(true);
//           setPipelines([]);
//           setDeals([]);
//           setColumns(FALLBACK_COLUMNS);
//           setSelectedPipelineId(null);
//           return;
//         }

//         const pRes = await apiGet(apiPath.pipedrivePipelines, {});
//         const pipelineList = asList(pRes);
//         setPipelines(pipelineList);

//         const pipelineId =
//           preferredPipelineId ||
//           selectedPipelineId ||
//           pickDefaultPipelineId(pipelineList);

//         if (!pipelineId) {
//           setColumns(FALLBACK_COLUMNS);
//           setDeals([]);
//           return;
//         }

//         setSelectedPipelineId(pipelineId);
//         await loadPipelineBoard(pipelineId, opts);
//       } catch (e) {
//         const status = e?.response?.status || e?.status;
//         const body = e?.response?.data || {};
//         const code = body?.code || e?.code;
//         const errText = String(body?.error || "");

//         const isPipedriveAuth =
//           status === 401 ||
//           status === 403 ||
//           code === "PIPEDRIVE_NOT_CONNECTED" ||
//           errText.includes("Scope and URL mismatch");

//         if (isPipedriveAuth) {
//           setNotConnected(true);
//           setPipelines([]);
//           setDeals([]);
//           setColumns(FALLBACK_COLUMNS);
//           setSelectedPipelineId(null);
//         } else {
//           setError(body?.message || e?.message || "Failed to load deals");
//         }
//       }
//     },
//     [loadPipelineBoard, selectedPipelineId]
//   );

//   useEffect(() => {
//     let cancelled = false;
//     (async () => {
//       setLoading(true);
//       await loadAll(null, { includeClosed: showClosed });
//       if (!cancelled) setLoading(false);
//     })();
//     return () => {
//       cancelled = true;
//     };
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//   }, []);

//   const handleSelectPipeline = async (id) => {
//     if (id == null) return;
//     setSelectedPipelineId(id);
//     setLoading(true);
//     setError(null);
//     try {
//       await loadPipelineBoard(id, { includeClosed: showClosed });
//     } catch (e) {
//       setError(e?.response?.data?.message || e.message);
//     } finally {
//       setLoading(false);
//     }
//   };

//   const handleRefresh = async () => {
//     setRefreshing(true);
//     try {
//       if (selectedPipelineId) {
//         await loadPipelineBoard(selectedPipelineId, { includeClosed: showClosed });
//       } else {
//         await loadAll(null, { includeClosed: showClosed });
//       }
//     } finally {
//       setRefreshing(false);
//     }
//   };

//   const handleToggleClosed = async () => {
//     const next = !showClosed;
//     setShowClosed(next);
//     if (selectedPipelineId) {
//       setLoading(true);
//       try { await loadDeals(selectedPipelineId, { includeClosed: next }); }
//       finally { setLoading(false); }
//     }
//   };

//   // AddDeal.js already POSTs to /v1/pipedrive/deals — only refresh UI here (do not post again)
//   const handleDealSaved = async (created) => {
//     try {
//       if (created?.id) {
//         setDeals((prev) => {
//           if (prev.some((d) => String(d.id) === String(created.id))) return prev;
//           return [...prev, mapDealFromApi(created)];
//         });
//       } else if (selectedPipelineId) {
//         await loadDeals(selectedPipelineId, { includeClosed: showClosed });
//       }
//       setIsAddDealOpen(false);
//     } catch (e) {
//       console.error("Refresh after create deal failed:", e);
//       if (selectedPipelineId) {
//         await loadDeals(selectedPipelineId, { includeClosed: showClosed });
//       }
//       setIsAddDealOpen(false);
//     }
//   };

//   // const handleDeleteDeal = async (deal) => {
//   //   if (!window.confirm(`Delete deal "${deal.title}"?`)) return;
//   //   try {
//   //     await apiPatch(`${apiPath.pipedriveDeals}/${deal.id}`, { status: "deleted" });
//   //     setDeals((prev) => prev.filter((d) => d.id !== deal.id));
//   //   } catch (e) {
//   //     alert(`Could not delete deal: ${e?.response?.data?.message || e.message}`);
//   //   }
//   // };

//   // const handleArchiveDeal = async (deal) => {
//   //   try {
//   //     await apiPatch(`${apiPath.pipedriveDeals}/${deal.id}`, { status: "deleted" });
//   //     setDeals((prev) => prev.filter((d) => d.id !== deal.id));
//   //   } catch (e) {
//   //     alert(`Could not archive deal: ${e?.response?.data?.message || e.message}`);
//   //   }
//   // };


//   const handleDeleteDeal = async (deal) => {
//   if (!window.confirm(`Delete deal "${deal.title}"?`)) return;
//   try {
//     await apiDelete(`${apiPath.pipedriveDeals}/${deal.id}`);
//     setDeals((prev) => prev.filter((d) => d.id !== deal.id));
//   } catch (e) {
//     alert(`Could not delete deal: ${e?.response?.data?.message || e.message}`);
//   }
// };

// const handleArchiveDeal = async (deal) => {
//   if (!window.confirm(`Archive deal "${deal.title}"?`)) return;
//   try {
//     await apiDelete(`${apiPath.pipedriveDeals}/${deal.id}`);
//     setDeals((prev) => prev.filter((d) => d.id !== deal.id));
//   } catch (e) {
//     alert(`Could not archive deal: ${e?.response?.data?.message || e.message}`);
//   }
// };

//   const handleDragStart = (e, deal) => {
//     if (movingDealIds.has(deal.id)) {
//       e.preventDefault();
//       return;
//     }
//     didDragRef.current = true;
//     setDraggingDealId(deal.id);
//     dragFromStageRef.current = deal.stageId;
//     e.dataTransfer.effectAllowed = "move";
//     e.dataTransfer.setData("text/plain", deal.id);
//     requestAnimationFrame(() => {
//       if (e.currentTarget) e.currentTarget.style.opacity = "0.45";
//     });
//   };

//   const handleDragEnd = (e) => {
//     if (e.currentTarget) e.currentTarget.style.opacity = "";
//     setDraggingDealId(null);
//     setDragOverStageId(null);
//     dragFromStageRef.current = null;
//     // Keep didDragRef true briefly so the trailing click does not open detail
//     setTimeout(() => {
//       didDragRef.current = false;
//     }, 0);
//   };

//   const handleColumnDragOver = (e, stageId) => {
//     e.preventDefault();
//     e.dataTransfer.dropEffect = "move";
//     if (dragOverStageId !== stageId) setDragOverStageId(stageId);
//   };

//   const handleColumnDragLeave = (e, stageId) => {
//     if (!e.currentTarget.contains(e.relatedTarget)) {
//       setDragOverStageId((cur) => (cur === stageId ? null : cur));
//     }
//   };

//   const moveDealToStage = async (dealId, toStageId) => {
//     const fromStageId = dragFromStageRef.current;
//     if (!dealId || !toStageId || String(fromStageId) === String(toStageId)) return;
//     if (movingDealIds.has(dealId)) return;

//     const prevDeals = deals;
//     setDeals((list) =>
//       list.map((d) => (d.id === dealId ? { ...d, stageId: String(toStageId) } : d))
//     );
//     setMovingDealIds((s) => new Set(s).add(dealId));
//     setDraggingDealId(null);
//     setDragOverStageId(null);

//     try {
//       await apiPatch(`${apiPath.pipedriveDeals}/${dealId}`, {
//         stage_id: Number(toStageId),
//       });
//     } catch (e) {
//       setDeals(prevDeals);
//       setError(
//         e?.response?.data?.message ||
//           e?.message ||
//           "Could not move deal in Pipedrive"
//       );
//     } finally {
//       setMovingDealIds((s) => {
//         const next = new Set(s);
//         next.delete(dealId);
//         return next;
//       });
//     }
//   };

//   const handleColumnDrop = async (e, toStageId) => {
//     e.preventDefault();
//     const dealId = e.dataTransfer.getData("text/plain") || draggingDealId;
//     await moveDealToStage(dealId, toStageId);
//   };

//   const sortedDeals = useMemo(() => {
//     const arr = [...deals];
//     if (sortBy === "value") arr.sort((a, b) => (Number(b.value) || 0) - (Number(a.value) || 0));
//     else if (sortBy === "title") arr.sort((a, b) => a.title.localeCompare(b.title));
//     else {
//       // arr.sort((a, b) => {
//       //   const av = a.nextActivityDate ? new Date(a.nextActivityDate).getTime() : Infinity;
//       //   const bv = b.nextActivityDate ? new Date(b.nextActivityDate).getTime() : Infinity;
//       //   return av - bv;
//       // });
//       arr.sort((a, b) => {
//   const av = new Date(a.addTime || a.stageChangeTime || 0).getTime();
//   const bv = new Date(b.addTime || b.stageChangeTime || 0).getTime();
//   return bv - av; // newest on top
// });
//     }
//     return arr;
//   }, [deals, sortBy]);

//   const dealsByStage = useMemo(() => {
//     const grouped = columns.reduce((acc, col) => {
//       acc[col.id] = [];
//       return acc;
//     }, {});
//     sortedDeals.forEach((deal) => {
//       if (grouped[deal.stageId]) grouped[deal.stageId].push(deal);
//     });
//     return grouped;
//   }, [sortedDeals, columns]);

//   const stageValue = (stageId) =>
//     (dealsByStage[stageId] || []).reduce((sum, d) => sum + (Number(d.value) || 0), 0);

//   const isOverdue = (d) => d.nextActivityDate && new Date(d.nextActivityDate) < new Date();

//   const getRottingInfo = (d) => {
//     const stage = columns.find((c) => String(c.id) === String(d.stageId));
//     const days = daysSince(d.stageChangeTime || d.updateTime || d.addTime);
//     if (days == null) return { days: null, isRotting: false };
//     const threshold = stage?.daysToRotten > 0 ? stage.daysToRotten : 7;
//     const rottingEnabled = stage?.rottenFlag !== false;
//     return { days, isRotting: rottingEnabled && days >= threshold };
//   };

//   const openDealDetail = (deal) => {
//     if (!deal?.id) return;
//     markLeadOpened(deal.id);
//     setOpenedTick((t) => t + 1);
//     navigate(`/pipedrive/deal-pipeline/${deal.id}`);
//   };

//   useEffect(() => {
//     const onFocus = () => setOpenedTick((t) => t + 1);
//     window.addEventListener("focus", onFocus);
//     return () => window.removeEventListener("focus", onFocus);
//   }, []);

//   return (
//     <div className="flex flex-col h-screen bg-white text-gray-800 font-sans overflow-hidden">

//       {/* TOP TOOLBAR */}
//       <header className="flex items-center justify-between px-4 py-3 bg-white border-b border-gray-200">
//         <div className="flex items-center space-x-3">
//           {/* Only 2 view icons: Pipeline + List */}
//           <div className="flex bg-white border border-gray-300 rounded overflow-hidden">
//             <button
//               onClick={() => setActiveView("kanban")}
//               className={`px-2 py-1.5 border-r border-gray-300 ${activeView === "kanban" ? "bg-blue-50 text-blue-600" : "text-gray-500 hover:bg-gray-50"}`}
//               title="Pipeline"
//             ><PipelineIcon /></button>
//             <button
//               onClick={() => setActiveView("list")}
//               className={`px-2 py-1.5 ${activeView === "list" ? "bg-blue-50 text-blue-600" : "text-gray-500 hover:bg-gray-50"}`}
//               title="List"
//             ><ListIcon /></button>
//           </div>

//           <div className="flex bg-[#317e3e] rounded overflow-hidden text-white shadow-sm">
//             <button onClick={() => setIsAddDealOpen(true)} className="px-3 py-1.5 text-sm font-semibold hover:bg-[#286834] flex items-center">
//               <PlusIcon className="mr-1 w-4 h-4" /> Deal
//             </button>
//           </div>
//         </div>

//         <div className="flex items-center space-x-4 text-sm">
//           <div className="flex items-center font-medium text-gray-700">
//             {deals.length} deals 
//           </div>
//           <button onClick={handleRefresh} disabled={refreshing} className={`p-1 rounded ${refreshing ? "text-gray-300" : "text-gray-500 hover:bg-gray-100"}`} title="Refresh">
//             {refreshing ? <SpinnerIcon /> : <RefreshIcon />}
//           </button>
         
//         </div>
//       </header>

//       {/* SUB TOOLBAR */}
//       <div className="flex items-center justify-end px-4 py-1.5 border-b border-gray-200 text-xs bg-white h-[34px]">
       
//         <div className="flex items-center space-x-4">
//           {deals.length > 0 && (
//             <Dropdown
//               align="right"
//               width="w-44"
//               trigger={
//                 <div className="flex items-center text-blue-600 font-medium cursor-pointer">
//                   <ArrowUpIcon />
//                   <span className="ml-1">
//                     Sort by: {sortBy === "value" ? "Value" : sortBy === "title" ? "Title" : "Next activity"}
//                   </span>
//                   <ChevronDownIcon className="ml-1" />
//                 </div>
//               }
//             >
//               <DropdownItem onClick={() => setSortBy("next_activity")}>Next activity</DropdownItem>
//               <DropdownItem onClick={() => setSortBy("value")}>Value</DropdownItem>
//               <DropdownItem onClick={() => setSortBy("title")}>Title</DropdownItem>
//             </Dropdown>
//           )}
//         </div>
//       </div>

//       {/* BANNERS */}
//       {notConnected && (
//         <div className="bg-yellow-50 border-b border-yellow-200 px-4 py-2 text-sm text-yellow-800 flex items-center justify-between">
//           <span>Pipedrive is not connected. Connect it to view deals.</span>
//           <a href="/pipedrive-connection" className="font-semibold underline">Go to Integration</a>
//         </div>
//       )}
//       {error && !notConnected && (
//         <div className="bg-red-50 border-b border-red-200 px-4 py-2 text-sm text-red-700 flex items-center justify-between">
//           <span>Error: {error}</span>
//           <button onClick={handleRefresh} className="font-semibold underline">Retry</button>
//         </div>
//       )}

//       {/* MAIN CONTENT */}
//       <div className="flex-1 overflow-hidden bg-white relative">

//         {/* KANBAN */}
//         {activeView === "kanban" && (
//           <div className="h-full overflow-x-auto no-scrollbar bg-[#f7f7f7] p-4">
//             <div className="flex h-full gap-3 items-start">
//               {columns.map((column) => {
//                 const isDropTarget = dragOverStageId === column.id && draggingDealId;
//                 return (
//                   <div
//                     key={column.id}
//                     className={`flex-shrink-0 ${KANBAN_COLUMN_WIDTH} rounded flex flex-col h-full max-h-full transition-colors ${
//                       isDropTarget ? "bg-[#dce8ff] ring-2 ring-blue-400 ring-inset" : "bg-[#ebebeb]"
//                     }`}
//                     onDragOver={(e) => handleColumnDragOver(e, column.id)}
//                     onDragLeave={(e) => handleColumnDragLeave(e, column.id)}
//                     onDrop={(e) => handleColumnDrop(e, column.id)}
//                   >
//                     <div className="px-3 pt-3 pb-2">
//                       <h2 className="font-semibold text-gray-800 text-sm truncate" title={column.title}>
//                         {column.title}
//                       </h2>
//                       <p className="text-xs text-gray-500 mt-1">
//                         ${stageValue(column.id)} · {(dealsByStage[column.id] || []).length}{" "}
//                         {(dealsByStage[column.id] || []).length === 1 ? "deal" : "deals"}
//                       </p>
//                     </div>
//                     <div className="flex-1 px-2 pb-2 space-y-2 overflow-y-auto no-scrollbar min-h-[80px]">
//                       {(dealsByStage[column.id] || []).length === 0 ? (
//                         <div className="flex items-center justify-center pt-6 pb-4 text-gray-400">
//                           {isDropTarget ? (
//                             <span className="text-xs text-blue-600 font-medium">Drop here</span>
//                           ) : (
//                             <button type="button" onClick={() => setIsAddDealOpen(true)}>
//                               <PlusIcon />
//                             </button>
//                           )}
//                         </div>
//                       ) : (
//                         dealsByStage[column.id].map((deal) => {
//                           const isMoving = movingDealIds.has(deal.id);
//                           const isDragging = draggingDealId === deal.id;
//                           const { days: rottingDays, isRotting } = getRottingInfo(deal);
//                           const noActivity = !deal.hasNextActivity;
//                           const showWarn = noActivity || isOverdue(deal);
//                           return (
//                             <div
//                               key={deal.id}
//                               draggable={!isMoving}
//                               onDragStart={(e) => handleDragStart(e, deal)}
//                               onDragEnd={handleDragEnd}
//                               onClick={() => {
//                                 if (didDragRef.current || isDragging || isMoving) return;
//                                 openDealDetail(deal);
//                               }}
//                               className={`w-full box-border p-3 rounded border select-none transition-shadow ${
//                                 isRotting
//                                   ? "bg-[#fce8e8] border-[#f0c4c4]"
//                                   : "bg-white border-gray-200 shadow-sm"
//                               } ${
//                                 isMoving
//                                   ? "opacity-60 cursor-wait"
//                                   : isDragging
//                                   ? "cursor-grabbing shadow-md"
//                                   : "cursor-pointer hover:shadow-md"
//                               }`}
//                             >
//                               {/* Title + person (Pipedrive stack) */}
//                               <p
//                                 className={`text-[13px] leading-snug font-semibold line-clamp-2 break-words ${
//                                   isRotting ? "text-[#8b3a3a]" : "text-[#21232c]"
//                                 }`}
//                               >
//                                 {deal.title}
//                               </p>
//                               {deal.personName ? (
//                                 <p
//                                   className={`text-xs mt-0.5 truncate ${
//                                     isRotting ? "text-[#a85a5a]" : "text-gray-500"
//                                   }`}
//                                 >
//                                   {deal.personName}
//                                 </p>
//                               ) : null}

//                               {/* Footer: person icon + value | rotting badge + warning */}
//                               <div className="flex items-center justify-between gap-2 mt-2.5 min-h-[18px]">
//                                 <div className="flex items-center gap-1.5 min-w-0 text-xs text-gray-500">
//                                   <PersonMiniIcon />
//                                   <span className="truncate">
//                                     {deal.currency || "USD"} {Number(deal.value) || 0}
//                                   </span>
//                                   {isMoving && <span className="text-gray-400 shrink-0">Saving…</span>}
//                                 </div>
//                                 <div className="flex items-center gap-1.5 shrink-0">
//                                   {isRotting && (
//                                     <span
//                                       title={`Rotting for ${rottingDays} days`}
//                                       className="inline-flex items-center justify-center px-1.5 h-[18px] rounded-full bg-[#c62828] text-white text-[10px] font-bold leading-none"
//                                     >
//                                       {rottingDays}d
//                                     </span>
//                                   )}
//                                   {showWarn && (
//                                     <span title={noActivity ? "No activity" : "Overdue"}>
//                                       <WarnIcon />
//                                     </span>
//                                   )}
//                                 </div>
//                               </div>
//                             </div>
//                           );
//                         })
//                       )}
//                     </div>
//                   </div>
//                 );
//               })}
//             </div>
//           </div>
//         )}

//         {/* LIST */}
//         {activeView === "list" && (
//           <ListView
//             deals={sortedDeals}
//             onAddDeal={() => setIsAddDealOpen(true)}
//             onImportDeals={() => alert("Import deals clicked")}
//             onDeleteDeal={handleDeleteDeal}
//             onArchiveDeal={handleArchiveDeal}
//             onOpenDeal={openDealDetail}
//             openedIds={openedIds}
//           />
//         )}
//       </div>

//       {/* ADD DEAL MODAL */}
//       {isAddDealOpen && (
//         <AddDeal onClose={() => setIsAddDealOpen(false)} onSave={handleDealSaved} />
//       )}

//       {/* Hidden scrollbar utility — scroll still works, bar not shown */}
//       <style>{`
//         .no-scrollbar::-webkit-scrollbar { display: none; }
//         .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
//       `}</style>
//     </div>
//   );
// }

// export default Deals;